import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";

import { useLocation, useNavigate, useParams } from "react-router";
import { assetService, cacheService } from "../../../Core/Api/services";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import SelectModeSubbarOpener from "../../Banners/SelectModeBanner/opener";
import { useTranslation } from "react-i18next";

import { DriveCenterPanel } from "./drive-center-panel";
import {
  DrivePanelState,
  SetIsSearch,
  SetLayout,
  SetPanelState,
  SetLoadingStates,
  GetCurrentObj,
  SetPanelObject,
  SetCurrentObject,
  ResetPanelObject,
  ResetDriveData,
} from "../../../Redux/Reducers/redux-slice-drive";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { routePaths } from "../../../Router/router";
import { GetPattern } from "../../../Redux/Reducers/redux-slice-filters";
import { CHILDREN_PAGE_SIZE, useSearchPagination } from "../helpers/use-drive-pagination";
import { useFolderPagination } from "../helpers/use-drive-pagination";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
export type DriveNavigateHandlerProps = {
  setSelectedItems: React.Dispatch<React.SetStateAction<DbAssetObjectInfos[]>>;
  selectedItems: DbAssetObjectInfos[];
  type: DriveType;
};

export enum DriveType {
  Browser = "browser",
  Title = "title",
}

export interface DriveContextContent {
  headObjectId: number;
  type: DriveType;
  basePath: string;
}

export const DriveContext = React.createContext<DriveContextContent | undefined>(undefined);

const DriveNavigateHandler: React.FC<DriveNavigateHandlerProps> = (props) => {
  const { t } = useTranslation();
  const { selectedItems, setSelectedItems, type } = props;
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const { loadFolderContents } = useFolderPagination();
  const { loadSearchContent } = useSearchPagination();

  // Redux
  const dispatch = useAppDispatch();
  const pattern = useAppSelector(GetPattern);
  const currentObject = useAppSelector(GetCurrentObj);

  // Conf
  const titleId: number | undefined = params.id ? parseInt(params.id) : undefined;
  const childId: number | undefined = params.childId ? parseInt(params.childId) : undefined;
  const [headObjectId, setHeadObjectId] = useState<number>(0);

  // Tree root container
  //   const [mainContentId, setMainContentId] = React.useState<number | undefined>();
  const [reload, setReload] = React.useState(0);

  useEffect(() => {
    dispatch(SetLoadingStates({ breadcrumbsLoading: true, treeLoading: true, folderLoading: true }));
    return () => {
      dispatch(ResetDriveData());
    };
  }, []);

  useEffect(() => {
    async function fetchHeadObjectId() {
      if (type === DriveType.Browser) {
        setHeadObjectId(cacheService.GetPlatformDriveHeadId()!);
      } else {
        if (!titleId) return;
        const tempHeadObjectId = await handleCreateOrFindUploadContainer(titleId);
        if (tempHeadObjectId !== undefined) setHeadObjectId(tempHeadObjectId);
      }
    }

    async function handleCreateOrFindUploadContainer(titleId: number) {
      if (!titleId) return undefined;

      try {
        let titleUploadContainer = await assetService.GetChildrenByObjectId(titleId, CHILDREN_PAGE_SIZE, [], 0, [
          ObjectType.UploadContainer,
        ]);

        if (titleUploadContainer.children && titleUploadContainer.children.length > 0) {
          if (titleUploadContainer.children.length > 1)
            console.error(`More than one UploadContainer found on title ${titleId}`);

          return titleUploadContainer.children[0].id;
        } else {
          enqueueCustomSnackbar("No upload container found", "error");
          navigate(-1);
          return undefined;
        }
      } catch (error) {
        enqueueCustomSnackbar(error as string, "error");
        return undefined;
      }
    }

    fetchHeadObjectId();
  }, [titleId, type]);

  useEffect(() => {
    const fetchAssetData = async () => {
      const currentId = childId ?? headObjectId;
      if (!currentId) return;

      setSelectedItems([]);
      dispatch(SetLoadingStates({ folderLoading: true }));

      try {
        // Fetch current virtual folder/asset
        const result = await assetService.GetObjectLightById(currentId, 0);

        if (result?.graph) {
          if (result.hierarchy) {
            const currentAsset = result.graph;

            // Asset is enlargeable: toggle enlarged panel
            // We set its parent as the fetched object during the tree population
            if (
              [
                ObjectType.Video,
                ObjectType.Audio,
                ObjectType.Document,
                ObjectType.Subtitle,
                ObjectType.Image,
                ObjectType.VideoClip,
              ].includes(currentAsset.type)
            ) {
              if (result.graph?.parentObjectId) {
                dispatch(SetPanelObject(result.graph));
                dispatch(SetPanelState(DrivePanelState.Enlarged));

                try {
                  const parent = await assetService.GetObjectLightById(result.graph.parentObjectId, 0);
                  if (parent.graph) {
                    dispatch(SetCurrentObject(parent.graph));
                  }
                } catch (error) {
                  console.error("Error fetching parent object:", error);
                }
              }
            } else if (
              [
                ObjectType.Folder,
                ObjectType.UploadContainer,
                ObjectType.GraphSkeleton,
                ObjectType.VirtualHead,
              ].includes(currentAsset.type)
            ) {
              // Asset is a folder, an uploadContainer (driveTitle root) or a graph skeleton (driveBrowser root): set it as Current obj and load its children
              dispatch(ResetPanelObject());
              dispatch(SetPanelState(DrivePanelState.Preview));
              dispatch(SetCurrentObject(result.graph));
            }
          }
        }
      } catch (error) {
        console.error("Error fetching asset:", error);
        navigate(routePaths.drive.path);
      }
    };

    fetchAssetData();
  }, [childId, headObjectId, reload]);

  useEffect(() => {
    if (pattern && pattern !== "") {
      dispatch(SetLoadingStates({ folderLoading: true }));
      dispatch(SetIsSearch(true));
      dispatch(ResetPanelObject());
      dispatch(SetPanelState(DrivePanelState.Closed));
      loadSearchContent({ page: 0, pageSize: CHILDREN_PAGE_SIZE }, true);
    }
  }, [pattern]);

  // Load current object on object change
  useEffect(() => {
    if (currentObject) loadFolderContents({ page: 0, pageSize: CHILDREN_PAGE_SIZE }, true);
  }, [currentObject]);

  // Effects
  useEffect(() => {
    if (location.state?.layout) {
      dispatch(SetLayout(location.state.layout));
    }
  }, [location.state?.layout]);

  // Reset Drive objects on unmount
  useEffect(() => {
    return () => {
      ResetDriveData();
    };
  }, []);

  //   useEffect(() => {
  //     // No data => Empty array
  //     if (!assetData || !assetData.hierarchyTree || !assetData.hierarchyTree.length) dispatch(SetHierarchyTree([]));
  //     // Access to all =>
  //     else if (!isDriveOnly) dispatch(SetHierarchyTree(assetData.hierarchyTree));
  //     else {
  //       // Find title
  //       const titleIndex = assetData!.hierarchyTree.findIndex((x) => x.objectLayer === ObjectLayer.Title);
  //       if (titleIndex === -1) dispatch(SetHierarchyTree(assetData.hierarchyTree));
  //       // Deep copy of array
  //       const output: HierarchyTree[] = JSON.parse(JSON.stringify(assetData.hierarchyTree));
  //       // Remove title
  //       const titleHiera = output.splice(titleIndex, 1);
  //       // Replace upload container name by title name
  //       output[titleIndex].objectName = titleHiera[0].objectName;

  //       dispatch(SetHierarchyTree(output));
  //     }
  //   }, [assetData?.hierarchyTree, isDriveOnly]);

  // Url params
  const mode = params.mode;
  const settings = params.settings;

  return (
    <DriveContext.Provider
      value={{
        headObjectId: headObjectId,
        type: type,
        basePath:
          type === DriveType.Title
            ? `${routePaths.title.path}/${params.id}/${routePaths.titleDrive.path}`
            : routePaths.drive.path,
      }}
    >
      <Box height="100%">
        <SelectModeSubbarOpener selectedObjects={selectedItems} />
        <DriveCenterPanel />
      </Box>
    </DriveContext.Provider>
  );
};

export default DriveNavigateHandler;
